import { FormItem } from "@/cs-ts-bridge/ts/form-item";
import { BaseElementDef, BaseElementDefBase, BaseControlDefBase } from "./BaseElementDef";
import { FormDefinitionItem } from "./__DefinitionDataInterface";
import { IdVerificationSettings } from "@cs-ts/id-verification-settings";
import { CalculationContext } from "./__CalculationContextInterface";
import { combineLatest, BehaviorSubject, Observable } from "rxjs";
import { map, switchMap, first } from "rxjs/operators";
import { IdVerificationTypeEnum } from "@cs-ts/id-verification-type-enum";
import { DocumentVerificationTypeEnum } from "@cs-ts/document-verification-type-enum";
import { KeyValue } from "@cs/KeyValue";
import FileDef from "./FileDef";
import { InvokeBody } from "@cs-ts/invoke-body";
import { VerificationResponse } from "@cs/VerificationResponse";
import { toUpperCase } from "@/public/lib/caseOperations";
import arrayEqual from "@/lib/arrayEquals";
import { PersonalVerificationSummary } from "@cs/PersonalVerificationSummary";
import flattenArrays from "@/lib/flattenArrays";
import generateGuid from "@/lib/generateGuid";
import { usePageStore } from "../pageState/store";
import { useMainStore } from "@/mainStore";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";
export interface FormVerificationItem extends FormItem {
    verificationResponse: string;
    verified: boolean | null;
}
const personRequestToSettingMap = {
    AddressLine1: "address1Id",
    AddressLine2: "address2Id",
    City: "cityId",
    DateOfBirth: "dateOfBirthId",
    FirstName: "firstNameId",
    LastName: "lastNameId",
    Phone: "phoneId",
    Ssn: "socialSecurityId",
    State: "stateId",
    ZipCode: "zipCodeId",
    Email: "emailId",
} as const;

export default class IdVerificationDef extends BaseElementDef<IdVerificationSettings, FormDefinitionItem<IdVerificationSettings>, FormItem> {
    declare associatedElements: string[];
    declare customErrorId: string;
    declare verificationResponse: string;
    declare verificationResponseRaw: { Success: boolean; Result: VerificationResponse } | null;
    declare _verified: boolean;
    declare _verified$: BehaviorSubject<boolean>;
    declare isPersonalIdVerification: boolean;
    declare validationMessages: string[];
    declare cacheKey$: BehaviorSubject<string>;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormItem, definitionData: FormDefinitionItem<IdVerificationSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFIdVerification"; // TODO - not hardcoded?
        this.customErrorId = this.vmId + "_Errors";
        /** @type {string[]} */
        this.associatedElements = this.associatedElements || [];
        this.isFocusable = true; // To properly get custom validation as invalid element
        this.isPersonalIdVerification = this.definition.settings.type == IdVerificationTypeEnum.PersonalIdentityVerification;
        this.cacheKey$ = new BehaviorSubject<string>("");
    }

    setData(data: FormVerificationItem, excludeChildren: boolean) {
        super.setData(data, excludeChildren);
        this.setKey(data, "verificationResponse");
        this.setKey(data, "verified", "_verified");
        this.setKey(data, "validationMessages");
        if (this.verificationResponse != null) {
            this.verificationResponseRaw = JSON.parse(this.verificationResponse);
        }
    }

    async verifyIdentity() {
        const data: KeyValue[] = [];
        const error$s: Observable<boolean>[] = [];
        const pageStore = usePageStore();
        const mainStore = useMainStore();
        const eliminateDuplicateSubmissions = await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.EliminateDuplicateSubmission.Id);
        for (const element$ of this.calculatedElement$s()) {
            const pipes: any = []; // eslint-disable-line @typescript-eslint/no-explicit-any
            if (this.isPersonalIdVerification) {
                pipes.push(
                    switchMap((children: BaseElementDefBase[]) => combineLatest(children.map(i => (i as BaseControlDefBase).effectiveValueList$()))),
                    map((valueLists: string[]) => Array.prototype.concat.apply([], valueLists)),
                );
            } else {
                pipes.push(map((children: FileDef[]) => children.map(i => i.valueId)));
            }
            const element = (await element$.pipe(first()).toPromise())![0];
            element.markEdited();
            error$s.push(element.errors$().pipe(map(i => i.length > 0)));
            const value = ((await element$.pipe.apply(element$, pipes).pipe(first()).toPromise()) as string[])[0]; //eslint-disable-line prefer-spread
            if (value != null) {
                data.push({ Key: element.id!, Value: value });
            }
        }
        const hasErrors = await combineLatest(error$s)
            .pipe(
                map(value => value.some(i => i)),
                first(),
            )
            .toPromise();
        if (hasErrors) {
            this.markEdited();
            throw Error("Error");
        }
        const adjuster = (postData: InvokeBody) => {
            postData.noWrite = false;
            if (eliminateDuplicateSubmissions && pageStore.formId == null) {
                postData.skipForm = false;
                postData.setUser = true;
            }
            return postData;
        };
        let response: {
            Success: boolean;
            Result: VerificationResponse;
        };
        if (eliminateDuplicateSubmissions) {
            const rawResponse = await this.invokeRawAsync("invokeIdVerification", JSON.stringify(data), adjuster);
            if (rawResponse?.Form != null) {
                pageStore._setFormId(rawResponse?.Form.formId);
            }
            response = JSON.parse(rawResponse.Result);
        } else {
            response = (await this.invokeAsync("invokeIdVerification", JSON.stringify(data), adjuster));
        }

        this.verificationResponseRaw = response;
        this.verificationResponse = JSON.stringify(this.verificationResponseRaw);
        this.setVerified(response.Success);
        this.cacheKey$.next(generateGuid());
        return response;
    }

    effectiveExtraData$(type: string, fallbackCalculation: (extraCacheKey$?: Observable<string>) => Observable<string[] | null>) {
        return fallbackCalculation(this.cacheKey$);
    }

    setVerified(state: boolean, silent: boolean = false) {
        this._verified = state;
        if (!silent) {
            this._verified$.next(this._verified);
        }
        return state;
    }

    verified$() {
        return combineLatest(this.calculatedElement$s()).pipe(
            switchMap(el => {
                const elements: BaseElementDefBase[] = Array.prototype.concat.apply([], el);
                if (this.isPersonalIdVerification) {
                    return combineLatest([this._verified$, combineLatest(elements.map(i => (i as BaseControlDefBase).effectiveValueList$().pipe(map(v => [i.id, v] as [string, string[]]))))]).pipe(
                        map(([_, values]) => {
                            if (this.verificationResponseRaw?.Result == null) return false;
                            const personRequestData = Object.entries(personRequestToSettingMap).reduce(
                                (acc, [requestProp, settingProp]) => {
                                    const settingId = this.definition.settings[settingProp];
                                    if (settingId != null) {
                                        acc[settingId] = this.verificationResponseRaw?.Result?.PersonalVerificationSummary?.PersonRequest?.[requestProp as keyof PersonalVerificationSummary["PersonRequest"]] ?? undefined;
                                    }
                                    return acc;
                                },
                                {} as Record<string, string | undefined>,
                            );
                            const currentVerifiedState = this.verificationResponseRaw.Success;
                            const result = values.every(([id, v]) => {
                                const value = Array.prototype.concat.apply([], v as string[])[0];
                                return toUpperCase(personRequestData[id]!) == toUpperCase(value);
                            });
                            this._edited$.next(result);
                            return this.setVerified(currentVerifiedState && result, true);
                        }),
                    );
                } else {
                    return combineLatest([this._verified$, combineLatest(elements.map(i => (i as FileDef).hasValue$().pipe(map(v => [(i as FileDef).valueId, v]))))]).pipe(
                        map(([verified, values]) => {
                            const valueIds = values.map(i => i[0]) as string[];
                            const hasValues = values.map(i => i[1]) as boolean[];
                            const responseSummary = this.verificationResponseRaw?.Result?.DocumentVerificationSummary;
                            const responseValueIds = [responseSummary?.Front?.ValueId, responseSummary?.Back?.ValueId].filter(i => !!i);
                            const result = arrayEqual(valueIds, responseValueIds) && hasValues.every(i => i);
                            this._edited$.next(result);
                            // Always make button editable whenever files are changed since we can't distinguish and compare exactly the previous and recent uploaded documents.
                            return this.setVerified(verified && result, true);
                        }),
                    );
                }
            }),
        );
    }

    calculatedElement$s() {
        const keys = this.isPersonalIdVerification ? (["firstNameId", "lastNameId", "socialSecurityId", "dateOfBirthId", "address1Id", "address2Id", "cityId", "stateId", "zipCodeId", "emailId", "phoneId"] as const) : this.definition.settings.documentType == DocumentVerificationTypeEnum.IdDriverLicense ? (["driverLicenseFrontId", "driverLicenseBackId"] as const) : (["passportId"] as const);
        return keys.filter(key => this.definition.settings[key] != null).map(key => this.calculationContext.rootElement!.getElementsById$(this.definition.settings[key]!, this.vmId));
    }

    calculatedElementsValid$() {
        return combineLatest(this.calculatedElement$s()).pipe(
            switchMap(elements => combineLatest(flattenArrays(elements).map(i => i.errors$().pipe(map(j => j.length == 0))))),
            map(valids => valids.every(i => i)),
        );
    }

    valid$(includeUnedited?: boolean, excludeChildren?: boolean) {
        const superValid$ = super.valid$(includeUnedited, excludeChildren);
        return combineLatest([superValid$, this._validateUnedited$(), this.edited$(), this.verified$()]).pipe(map(([superValid, validateUnedited, edited, verified]) => (superValid && (edited || validateUnedited) ? verified : true)));
    }

    errors$() {
        return this.valid$().pipe(map(valid => (valid ? [] : this.validationMessages.length > 0 ? this.validationMessages : ["Must pass ID Verification to continue."])));
    }

    blockSubmit$() {
        return combineLatest([this.verified$(), this.visible$()]).pipe(map(([verified, visible]) => !verified && visible));
    }
}
